import * as React from 'react';
import { TextInput } from '@leagueplatform/genesis-core';
import { useLocales } from '@web-config-app/core-react';
import { Tag } from '@web-config-app/core-react-ui';
import type { ControlComponent } from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import { getPlainTextControlHandlers } from '../../../hooks/plain-text-control/use-plain-text-control.hook';
import { HiddenUnicodeWarning } from '../hidden-unicode/hidden-unicode-warning.component';
import { getInputStatusWithHiddenUnicodeWarning } from '../hidden-unicode/get-input-status-with-hidden-unicode-warning.util';

export const TextInputControl: ControlComponent = (props) => {
  const { currentLocale, hasMultipleLocales } = useLocales();

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        handleChange,
        path,
        schema,
        required,
        inputStatus,
        enabled,
        localized,
        id,
      }) => {
        const { pattern, maxLength, minLength, format } = schema;
        const warningMessageId = `${id}-hidden-unicode-warning`;
        const {
          hasHiddenCharacters,
          handleBlur,
          handleChange: handlePlainTextChange,
          handleClear,
        } = getPlainTextControlHandlers({
          value: data ?? '',
          path,
          onChange: handleChange,
        });

        /**
         * We may want to, in the future, implement a specific EmailInputControl but, in the meantime
         * JSON Schema supports two email `format` options, as defined
         * here: https://json-schema.org/understanding-json-schema/reference/string#email-addresses
         */
        const inputType = ['email', 'idn-email'].includes(format ?? '')
          ? 'email'
          : undefined;
        const resolvedInputStatus = getInputStatusWithHiddenUnicodeWarning(
          inputStatus,
          hasHiddenCharacters,
        );

        return (
          <>
            <TextInput
              value={data ?? ''}
              readOnly={!enabled}
              required={required}
              id={id}
              pattern={pattern}
              minLength={minLength}
              maxLength={maxLength}
              type={inputType}
              inputStatus={resolvedInputStatus}
              aria-describedby={
                hasHiddenCharacters
                  ? `${id}-hint ${warningMessageId}`
                  : `${id}-hint`
              }
              clearable={enabled && data && data.length > 0}
              onChange={(e) => handlePlainTextChange(e.currentTarget.value)}
              onClear={handleClear}
              onBlur={(e) => handleBlur(e.currentTarget.value)}
              trailingContent={
                localized && hasMultipleLocales ? (
                  <Tag label={currentLocale} />
                ) : undefined
              }
              css={{
                backgroundColor: '$inputBackgroundDefault',
              }}
            />
            <HiddenUnicodeWarning
              id={id}
              currentValue={data ?? ''}
              path={path}
              enabled={enabled}
              onChange={handleChange}
            />
          </>
        );
      }}
    />
  );
};
